import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/auth';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (name: string, email: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Vérifier l'utilisateur au chargement
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      checkAuthenticatedUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Écouter l'événement de déconnexion forcée (401 intercepté par Axios)
  useEffect(() => {
    const handleForceLogout = () => {
      setUser(null);
    };
    globalThis.addEventListener('auth:logout', handleForceLogout);
    return () => {
      globalThis.removeEventListener('auth:logout', handleForceLogout);
    };
  }, []);

  const checkAuthenticatedUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error: any) {
      console.error('Erreur lors de la vérification de l\'utilisateur:', error);
      // Seulement supprimer le token si c'est une erreur d'authentification
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await authService.login(email, password);
      
      if (response.success && response.data) {
        localStorage.setItem('auth_token', response.data.token);
        setUser(response.data.user);
        return { success: true, message: 'Connexion réussie' };
      }
      
      return { success: false, message: response.message || 'Erreur de connexion' };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erreur de connexion' 
      };
    }
  };

  const register = async (name: string, email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await authService.register(name, email, password);
      
      if (response.success && response.data) {
        localStorage.setItem('auth_token', response.data.token);
        setUser(response.data.user);
        return { success: true, message: 'Inscription réussie' };
      }
      
      return { success: false, message: response.message || 'Erreur d\'inscription' };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erreur d\'inscription' 
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
